import { useCallback, useEffect, useRef, useState } from 'react';

interface Opcoes {
  /** Se false, não busca (ex.: aguardando login). Padrão true. */
  ativo?: boolean;
  /** Intervalo de polling em ms (0 = desliga). Padrão 0. */
  intervalo?: number;
  /** Tempo mínimo entre revalidações por foco, em ms. Padrão 15s. */
  intervaloMinimoFoco?: number;
}

interface Resultado<T> {
  dados: T | null;
  carregando: boolean;
  erro: string | null;
  /** Refaz a busca manualmente (ex.: após uma ação do usuário). */
  recarregar: () => void;
}

/**
 * Busca de dados "viva" para o portal (que não usa React Query).
 *
 * Hot reload: além de buscar no mount e quando `deps` mudam, revalida
 * automaticamente quando o usuário volta para a aba/janela (`focus` /
 * `visibilitychange`) e, opcionalmente, em um intervalo de polling.
 *
 * Use em páginas que exibem dados (listas/detalhes) — não em formulários,
 * que perderiam o estado se recarregados.
 */
export function useDadosVivos<T>(
  buscar: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
  opcoes: Opcoes = {},
): Resultado<T> {
  const { ativo = true, intervalo = 0, intervaloMinimoFoco = 15_000 } = opcoes;

  const [dados, setDados] = useState<T | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // Guarda a função de busca mais recente sem virar dependência dos efeitos.
  const buscarRef = useRef(buscar);
  buscarRef.current = buscar;

  const montadoRef = useRef(false);
  const ultimaBuscaRef = useRef(0);
  const buscandoRef = useRef(false);

  const executar = useCallback(async (silencioso: boolean) => {
    if (!ativo || buscandoRef.current) return;
    buscandoRef.current = true;
    if (!silencioso) setCarregando(true);
    try {
      const r = await buscarRef.current();
      if (!montadoRef.current) return;
      setDados(r);
      setErro(null);
      ultimaBuscaRef.current = Date.now();
    } catch (e) {
      if (!montadoRef.current) return;
      setErro(e instanceof Error ? e.message : 'Falha ao carregar.');
    } finally {
      if (montadoRef.current) setCarregando(false);
      buscandoRef.current = false;
    }
  }, [ativo]);

  // Mount + mudança de deps + (de)ativação.
  useEffect(() => {
    montadoRef.current = true;
    if (ativo) executar(false);
    return () => { montadoRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativo, ...deps]);

  // Revalida ao focar a aba/janela (com janela mínima para não martelar).
  useEffect(() => {
    if (!ativo) return;
    function aoFocar() {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - ultimaBuscaRef.current < intervaloMinimoFoco) return;
      executar(true);
    }
    window.addEventListener('focus', aoFocar);
    document.addEventListener('visibilitychange', aoFocar);
    return () => {
      window.removeEventListener('focus', aoFocar);
      document.removeEventListener('visibilitychange', aoFocar);
    };
  }, [ativo, intervaloMinimoFoco, executar]);

  // Polling opcional.
  useEffect(() => {
    if (!ativo || intervalo <= 0) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') executar(true);
    }, intervalo);
    return () => window.clearInterval(id);
  }, [ativo, intervalo, executar]);

  const recarregar = useCallback(() => { executar(false); }, [executar]);

  return { dados, carregando, erro, recarregar };
}

/**
 * Variante para páginas que já têm a própria função de carregamento (com vários
 * estados). Revalida ao focar a aba/janela e, opcionalmente, em polling, sem
 * substituir a lógica de fetch existente.
 */
export function useRevalidarAoFocar(
  recarregar: () => void,
  opcoes: { ativo?: boolean; intervalo?: number; intervaloMinimoFoco?: number } = {},
): void {
  const { ativo = true, intervalo = 0, intervaloMinimoFoco = 15_000 } = opcoes;
  const recarregarRef = useRef(recarregar);
  recarregarRef.current = recarregar;
  const ultimaRef = useRef(Date.now());

  useEffect(() => {
    if (!ativo) return;
    function aoFocar() {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - ultimaRef.current < intervaloMinimoFoco) return;
      ultimaRef.current = Date.now();
      recarregarRef.current();
    }
    window.addEventListener('focus', aoFocar);
    document.addEventListener('visibilitychange', aoFocar);
    let id = 0;
    if (intervalo > 0) {
      id = window.setInterval(() => {
        if (document.visibilityState === 'visible') {
          ultimaRef.current = Date.now();
          recarregarRef.current();
        }
      }, intervalo);
    }
    return () => {
      window.removeEventListener('focus', aoFocar);
      document.removeEventListener('visibilitychange', aoFocar);
      if (id) window.clearInterval(id);
    };
  }, [ativo, intervalo, intervaloMinimoFoco]);
}
